// Types
// Components
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { allowedFieldTypeMatching } from 'constants/Field.constants';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
// Utils
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import _ from 'lodash';
// Slices
import { fieldMappingSelector } from 'slices/field.slice';
import {
  checkIfFirstFieldRelationExists,
  copyFieldRelation,
  createUnsavedFieldRelation,
  deleteFieldRelation,
  fieldRelationMappingSelector,
  fieldRelationsLoading,
  fieldRelationsSelector,
  selectedFieldRelationIdsSelector,
  setFirstFieldRelation,
  setSecondFieldRelation,
} from 'slices/fieldRelation.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { matchString } from 'utils/stringUtils';
import type { TField, TFieldRelation } from '@timeedit/types';
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
  type TEIOption,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Styles
import './FieldRelationList.scss';

interface IRowData {
  id: number;
  name: string;
  types: string;
}

interface Props {
  confirmDiscardFieldRelationChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const FieldRelationList = ({ confirmDiscardFieldRelationChanges }: Props) => {
  const dispatch = useAppDispatch();

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const fieldRelations: TFieldRelation[] = useAppSelector(
    fieldRelationsSelector
  );
  const fieldRelationMapping: Record<number, TFieldRelation> = useAppSelector(
    fieldRelationMappingSelector
  );
  const fieldMapping: Record<number, TField> =
    useAppSelector(fieldMappingSelector);
  const loading = useAppSelector(fieldRelationsLoading);
  const selectedFieldRelationIds = useAppSelector(
    selectedFieldRelationIdsSelector
  );

  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  useEffect(() => {
    setTimeout(() => setHighlightIds(selectedFieldRelationIds), 100);
  }, [selectedFieldRelationIds.join('-')]);

  const sourceData: IRowData[] = fieldRelations.map(
    (fieldRelation: TFieldRelation) => ({
      id: fieldRelation.id,
      name: fieldRelation.name || getText('new_field_relation'),
      types: `${getText(
        _.get(fieldMapping, [fieldRelation.sourceField || 0, 'fieldType'], '')
      )} - ${getText(
        _.get(fieldMapping, [fieldRelation.matchField || 0, 'fieldType'], '')
      )}`,
    })
  );
  const filterSortData = useMemo(() => {
    const {
      filters: { type },
      freeTextSearch,
    } = filterSettings;
    return sourceData.filter((row: IRowData) => {
      let matchType = true;
      let matchFreeText = true;
      if (type !== undefined) {
        matchType = type === row.types;
      }
      if (freeTextSearch) {
        matchFreeText = matchString(row.name, freeTextSearch);
      }
      return matchType && matchFreeText;
    });
  }, [sourceData, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardFieldRelationChanges(true, () =>
      dispatch(createUnsavedFieldRelation())
    );
  }, []);

  const onDelete = useCallback(
    (record: IRowData) => {
      confirmDelete(record.name, () =>
        dispatch(deleteFieldRelation(record.id))
      );
    },
    [confirmDelete, dispatch]
  );

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardFieldRelationChanges(true, () =>
        dispatch(copyFieldRelation(fieldRelationMapping[record.id]))
      );
    },
    [fieldRelationMapping]
  );

  const onRowClick = useCallback(
    ({ id }: IRowData) => {
      confirmDiscardFieldRelationChanges(true, () =>
        dispatch(setFirstFieldRelation(fieldRelationMapping[id]))
      );
    },
    [fieldRelationMapping]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onCompare = useCallback(
    (record: IRowData) => {
      const isFirstFieldRelationExists = dispatch(
        checkIfFirstFieldRelationExists()
      );
      if (!isFirstFieldRelationExists) {
        return dispatch(setFirstFieldRelation(fieldRelationMapping[record.id]));
      }
      confirmDiscardFieldRelationChanges(false, () =>
        dispatch(setSecondFieldRelation(fieldRelationMapping[record.id]))
      );
    },
    [fieldRelationMapping]
  );

  const getDrawer = useCallback(
    () => dispatch(checkIfFirstFieldRelationExists()),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen: getDrawer,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, getDrawer]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button size="small" type="primary" onClick={onCreate}>
          Create new
        </Button>
      </Space>
    ),
    [onCreate]
  );

  const title = useTableTitle<IRowData>({
    total: fieldRelations.length,
    filteredAmount: filterSortData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="field-relation-list" ref={contentRef}>
      <Filter
        settings={filterSettings}
        onChange={setFilterSettings}
        defaultFields={defautlFilters}
        options={options}
      />
      {/* The loading prop of the table causes all rows re-rendered whenenver it changed (quite intensive operation) */}
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterSortData}
          columns={columns}
          onRow={onRow}
          footer={footer}
          getMenuProps={menuProps}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(FieldRelationList);

const columns = [
  {
    title: 'Name',
    key: 'name',
    dataIndex: 'name',
    ellipsis: true,
    sorter: (a: IRowData, b: IRowData) => a.name.localeCompare(b.name),
  },
  {
    title: 'Type',
    key: 'types',
    dataIndex: 'types',
    ellipsis: true,
    sorter: (a: IRowData, b: IRowData) => a.types.localeCompare(b.types),
  },
];

const defautlFilters: TEIFilterField[] = [
  {
    key: 'type',
    label: 'Type',
    type: TEEFilterType.text,
  },
];
const options: Record<string, TEIOption[]> = {
  type: allowedFieldTypeMatching
    .map(([type1, type2]) => {
      const type1Text = getText(type1);
      const type2Text = getText(type2);
      return {
        value: `${type1Text} - ${type2Text}`,
        label: `${type1Text} - ${type2Text}`,
      };
    })
    .sort((option1, option2) => option1.label.localeCompare(option2.label)),
};
